import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Typography,
  message,
  Checkbox
} from 'antd';
import { 
  LeftOutlined,
  RightOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import EventTypeService from '../../services/EventTypeService';
import BookingService from '../../services/BookingService';
import FormsService from '../../services/FormsService';
import OnDemandStorageUnitService from '../../services/OnDemandStorageUnitService';
import { COLORS } from '../../constants/colors';
import { Step1EventSelection, Step2UserInfo, Step3CustomForms } from './steps';
import { DoubleBookConfirmModal, BlockedDateConfirmModal } from './ConfirmationModals';

const { Text } = Typography;

const CreateBookingModal = ({ visible, onCancel, onSuccess, forceMobileEvent = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null); // For multi-day bookings
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [hasCustomForms, setHasCustomForms] = useState(false);
  const [customForms, setCustomForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [formResponses, setFormResponses] = useState({});
  const [createAnother, setCreateAnother] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  const [allowDoubleBook, setAllowDoubleBook] = useState(false);
  const [doubleBookConfirmVisible, setDoubleBookConfirmVisible] = useState(false);
  const [pendingDoubleBookSlot, setPendingDoubleBookSlot] = useState(null);
  const [isSelectedDateBlocked, setIsSelectedDateBlocked] = useState(false);
  const [blockedDateConfirmVisible, setBlockedDateConfirmVisible] = useState(false);
  const [pendingBlockedDate, setPendingBlockedDate] = useState(null);
  const [storageUnits, setStorageUnits] = useState([]);
  const [form] = Form.useForm();
  const [formsForm] = Form.useForm();
  
  // Watch form values for real-time updates in booking summary
  const formValues = Form.useWatch([], form);

  // Reset modal state when closed
  useEffect(() => {
    if (!visible) {
      setCurrentStep(0);
      setSelectedEventType(null);
      setSelectedDate(null);
      setSelectedEndDate(null);
      setSelectedTimeSlot(null);
      setAvailableSlots([]);
      setHasCustomForms(false);
      setCustomForms([]);
      setFormResponses({});
      setCreateAnother(false);
      setAllowDoubleBook(false);
      setDoubleBookConfirmVisible(false);
      setPendingDoubleBookSlot(null);
      setIsSelectedDateBlocked(false);
      setBlockedDateConfirmVisible(false);
      setPendingBlockedDate(null);
      form.resetFields();
      formsForm.resetFields();
    } else {
      loadEventTypes();
      loadStorageUnits();
    }
  }, [visible]);

  // Load event types
  const loadEventTypes = async () => {
    try {
      setLoading(true);
      const response = await EventTypeService.getEventTypes();
      if (response.success) {
        setEventTypes(response.data.eventTypes || []);
      } else {
        message.error('Failed to load event types');
      }
    } catch (error) {
      console.error('Error loading event types:', error);
      message.error('Failed to load event types');
    } finally {
      setLoading(false);
    }
  };

  const loadStorageUnits = async () => {
    try {
      const response = await OnDemandStorageUnitService.getStorageUnits({ isActive: true });
      const payload = response?.data?.data || response?.data || {};
      setStorageUnits(payload.storageUnits || payload || []);
    } catch (error) {
      console.error('Error loading storage units:', error);
    }
  };

  // Load available time slots when date is selected
  const loadAvailableSlots = async (date) => {
    if (!selectedEventType || !date) return;

    try {
      setLoadingSlots(true);
      const response = await BookingService.getAdminAvailability(
        selectedEventType._id,
        date.format('YYYY-MM-DD')
      );

      if (response.success) {
        const slots = response.data.timeSlots || [];
        setAvailableSlots(slots);
        if (slots.length === 0) {
          message.warning('No time slots available for this date');
        }
      } else {
        setAvailableSlots([]);
        message.warning('No available slots for this date');
      }
    } catch (error) {
      console.error('Error loading slots:', error);
      message.error('Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  // Load blocked dates for selected event type
  const loadBlockedDates = async (eventTypeId) => {
    try {
      const response = await EventTypeService.getEventTypeBlockedDates(eventTypeId);
      if (response.success && response.data) {
        const dates = response.data.data || response.data || [];
        setBlockedDates(dates);
      } else {
        setBlockedDates([]);
      }
    } catch (error) {
      console.error('Error loading blocked dates:', error);
      setBlockedDates([]);
    }
  };

  // Check if a date is blocked
  const isDateBlocked = (date) => {
    if (!blockedDates || blockedDates.length === 0) return false;
    const dateStr = date.format('YYYY-MM-DD');
    return blockedDates.some(block => {
      const blockStartStr = moment.utc(block.startDate).format('YYYY-MM-DD');
      const blockEndStr = moment.utc(block.endDate).format('YYYY-MM-DD');
      return dateStr >= blockStartStr && dateStr <= blockEndStr;
    });
  };

  // Custom cell renderer for calendar
  const dateCellRender = (date) => {
    const isBlocked = isDateBlocked(date);
    const isToday = date.isSame(moment(), 'day');
    const isSelected = selectedDate && date.isSame(selectedDate, 'day');
    const isPast = date.isBefore(moment(), 'day');
    
    return (
      <div style={{ 
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'flex-start', paddingTop: '2px'
      }}>
        {isBlocked && !isPast && (
          <div style={{
            width: '6px', height: '6px', background: '#ff4d4f',
            borderRadius: '50%', marginBottom: '2px'
          }} />
        )}
        {isToday && !isSelected && (
          <div style={{
            position: 'absolute', top: '4px', left: '4px', right: '4px', bottom: '4px',
            border: '2px solid ' + COLORS.$primaryBlue,
            borderRadius: '50%', pointerEvents: 'none'
          }} />
        )}
      </div>
    );
  };

  // Handle event type selection
  const handleEventTypeSelect = (eventType) => {
    setSelectedEventType(eventType);
    setSelectedDate(null);
    setSelectedEndDate(null);
    setSelectedTimeSlot(null);
    setAvailableSlots([]);
    loadBlockedDates(eventType._id);
  };

  // Handle date selection
  const handleDateSelect = (date) => {
    const blocked = isDateBlocked(date);
    if (blocked) {
      setPendingBlockedDate(date);
      setBlockedDateConfirmVisible(true);
    } else {
      setSelectedDate(date);
      setSelectedEndDate(null); // Reset end date when start date changes
      setSelectedTimeSlot(null);
      setIsSelectedDateBlocked(false);
      loadAvailableSlots(date);
    }
  };

  // Handle date cell click from calendar
  const onDateSelect = (date) => {
    const isPast = date.isBefore(moment(), 'day');
    if (isPast) return;
    
    const isBlocked = isDateBlocked(date);
    if (isBlocked) {
      setPendingBlockedDate(date);
      setBlockedDateConfirmVisible(true);
    } else {
      setIsSelectedDateBlocked(false);
      handleDateSelect(date);
    }
  };

  // Handle end date selection for multi-day bookings
  const handleEndDateSelect = (date) => {
    if (date && selectedDate && date.isAfter(selectedDate, 'day')) {
      setSelectedEndDate(date);
    } else {
      setSelectedEndDate(null); // Clear if invalid or same as start
    }
  };

  // Confirm booking on blocked date
  const handleConfirmBlockedDate = () => {
    setSelectedDate(pendingBlockedDate);
    setSelectedEndDate(null);
    setSelectedTimeSlot(null);
    setIsSelectedDateBlocked(true);
    loadAvailableSlots(pendingBlockedDate);
    setBlockedDateConfirmVisible(false);
    setPendingBlockedDate(null);
  };

  // Handle time slot selection
  const handleTimeSlotSelect = (slot) => {
    if (slot.isBooked) {
      setPendingDoubleBookSlot(slot);
      setDoubleBookConfirmVisible(true);
    } else {
      setSelectedTimeSlot(slot);
      setAllowDoubleBook(false);
    }
  };

  // Confirm double-booking
  const handleConfirmDoubleBook = () => {
    setSelectedTimeSlot(pendingDoubleBookSlot);
    setAllowDoubleBook(true);
    setDoubleBookConfirmVisible(false);
    setPendingDoubleBookSlot(null);
  };

  // Load custom forms for the selected event type
  const loadCustomForms = async () => {
    if (!selectedEventType?.associatedForms || selectedEventType.associatedForms.length === 0) {
      setHasCustomForms(false);
      return;
    }

    try {
      setLoadingForms(true);
      const formIds = selectedEventType.associatedForms.map(af => af.formId);
      const formPromises = formIds.map(formId => FormsService.getPublicFormById(formId));
      const formResults = await Promise.all(formPromises);
      const loadedForms = formResults.filter(result => result.success).map(result => result.data);
      setCustomForms(loadedForms);
      setHasCustomForms(loadedForms.length > 0);
    } catch (error) {
      console.error('Error loading forms:', error);
      message.error('Failed to load custom forms');
      setHasCustomForms(false);
    } finally {
      setLoadingForms(false);
    }
  };

  // Handle step navigation
  const handleNext = async () => {
    if (currentStep === 0) {
      if (!selectedEventType) { message.warning('Please select an event type'); return; }
      if (!selectedDate) { message.warning('Please select a date'); return; }
      if (!selectedTimeSlot) { message.warning('Please select a time slot'); return; }
      setCurrentStep(1);
    } else if (currentStep === 1) {
      try {
        await form.validateFields();
        if (selectedEventType?.associatedForms && selectedEventType.associatedForms.length > 0) {
          await loadCustomForms();
          setCurrentStep(2);
        } else {
          handleSubmit();
        }
      } catch (error) {
        message.warning('Please fill in all required fields');
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  // Handle form submission
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Validate time slot is still available
      try {
        const availabilityCheck = await EventTypeService.getPublicAvailability(
          selectedEventType._id,
          selectedDate.format('YYYY-MM-DD')
        );
        
        if (availabilityCheck.success && availabilityCheck.data.availability) {
          const stillAvailable = availabilityCheck.data.availability.some(
            slot => slot.startTime === selectedTimeSlot.startTime && slot.available
          );
          
          if (!stillAvailable) {
            message.warning('Selected time slot is no longer available. Please choose another time.');
            setCurrentStep(0);
            setLoading(false);
            return;
          }
        }
      } catch (validationError) {
        console.warn('Could not validate time slot availability:', validationError);
      }

      // Prepare booking data
      const bookingData = {
        eventTypeId: selectedEventType._id,
        timeSlot: {
          startTime: selectedTimeSlot.startTime,
          endTime: selectedTimeSlot.endTime
        },
        userInfo: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          customData: {}
        },
        eventLocation: {
          eventName: values.eventName,
          address: values.address,
          city: values.city,
          state: values.state,
          zipCode: values.zipCode
        },
        adminNotes: values.adminNotes,
        priority: values.priority || 'normal',
        skipConfirmationEmail: values.skipConfirmationEmail || false,
        createdByAdmin: true,
        allowDoubleBook: allowDoubleBook,
        // Include endDate for multi-day bookings
        ...(selectedEndDate ? { endDate: selectedEndDate.format('YYYY-MM-DD') } : {})
      };

      // Include custom form responses if they exist
      if (hasCustomForms && Object.keys(formResponses).length > 0) {
        bookingData.formResponses = formResponses;
        bookingData.userInfo.customData = {
          ...bookingData.userInfo.customData,
          formResponses
        };
      }

      const response = await BookingService.createAdminBooking(bookingData);

      if (response.success) {
        // If this booking is flagged as a mobile event, publish it to the
        // Deployed Hearts mobile app by creating the linked OnDemandEvent.
        if (values.mobileEvent) {
          const newBooking = response.data?.booking || response.data?.data || response.data;
          const newBookingId = newBooking?._id || newBooking?.id;
          if (newBookingId) {
            const appResult = await BookingService.toggleAppVisibility(newBookingId, true, {
              payRate: values.payRate ?? 0,
              payType: values.payType || 'hourly',
              estimatedHours: values.estimatedHours ?? 0,
              requiredStaff: values.requiredStaff ?? 1,
              estimatedAttendees: values.estimatedAttendees ?? 0,
              ...(values.primaryStorageUnit ? { primaryStorageUnit: values.primaryStorageUnit } : {}),
              ...(values.backupStorageUnit ? { backupStorageUnit: values.backupStorageUnit } : {}),
              ...(values.geofenceRadius !== undefined ? { geofenceRadius: values.geofenceRadius } : {})
            });
            if (!appResult.success) {
              message.warning(`Booking created, but publishing to the mobile app failed: ${appResult.error}`);
            }
          } else {
            message.warning('Booking created, but could not publish to the mobile app (missing booking id).');
          }
        }

        message.success(
          createAnother 
            ? 'Booking created! You can create another one.' 
            : 'Booking created successfully!',
          5
        );
        
        if (createAnother) {
          setCurrentStep(0);
          setSelectedEventType(null);
          setSelectedDate(null);
          setSelectedEndDate(null);
          setSelectedTimeSlot(null);
          setAvailableSlots([]);
          setFormResponses({});
          setHasCustomForms(false);
          setCustomForms([]);
          form.resetFields();
          formsForm.resetFields();
          onSuccess();
        } else {
          onSuccess();
          onCancel();
        }
      } else {
        const errorMsg = response.error || response.message || 'Failed to create booking';
        if (errorMsg.toLowerCase().includes('conflict') || errorMsg.toLowerCase().includes('no longer available')) {
          message.error({
            content: 'Time slot conflict detected. The selected time is no longer available. Please choose another time.',
            duration: 6
          });
          setCurrentStep(0);
        } else {
          message.error(errorMsg);
        }
      }
    } catch (error) {
      if (error.errorFields) {
        message.warning('Please fill in all required fields');
      } else {
        console.error('Error creating booking:', error);
        const errorMsg = error.response?.data?.message || error.message || 'Failed to create booking';
        message.error({ content: errorMsg, duration: 5 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircleOutlined style={{ color: COLORS.$primaryBlue }} />
          <span>Create New Booking</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={null}
      destroyOnClose
    >
      {currentStep === 0 && (
        <Step1EventSelection
          loading={loading}
          eventTypes={eventTypes}
          selectedEventType={selectedEventType}
          selectedDate={selectedDate}
          selectedEndDate={selectedEndDate}
          selectedTimeSlot={selectedTimeSlot}
          availableSlots={availableSlots}
          loadingSlots={loadingSlots}
          isSelectedDateBlocked={isSelectedDateBlocked}
          handleEventTypeSelect={handleEventTypeSelect}
          onDateSelect={onDateSelect}
          handleEndDateSelect={handleEndDateSelect}
          handleTimeSlotSelect={handleTimeSlotSelect}
          dateCellRender={dateCellRender}
        />
      )}
      {currentStep === 1 && (
        <Step2UserInfo
          form={form}
          formValues={formValues}
          selectedDate={selectedDate}
          selectedEndDate={selectedEndDate}
          selectedTimeSlot={selectedTimeSlot}
          selectedEventType={selectedEventType}
          storageUnits={storageUnits}
          forceMobileEvent={forceMobileEvent}
        />
      )}
      {currentStep === 2 && hasCustomForms && (
        <Step3CustomForms
          loadingForms={loadingForms}
          customForms={customForms}
          formsForm={formsForm}
          formResponses={formResponses}
          setFormResponses={setFormResponses}
          formValues={formValues}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
        />
      )}

      {/* Footer Buttons */}
      <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
        {((currentStep === 1 && !hasCustomForms) || (currentStep === 2 && hasCustomForms)) && (
          <div style={{ marginBottom: '12px' }}>
            <Checkbox 
              checked={createAnother}
              onChange={(e) => setCreateAnother(e.target.checked)}
            >
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Create another booking after this one
              </Text>
            </Checkbox>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {currentStep > 0 && (
              <Button icon={<LeftOutlined />} onClick={handleBack}>Back</Button>
            )}
            {currentStep === 0 && (
              <Button 
                type="primary" 
                icon={<RightOutlined />} 
                onClick={handleNext}
                disabled={!selectedEventType || !selectedDate || !selectedTimeSlot}
              >
                Next
              </Button>
            )}
            {currentStep === 1 && (
              <Button 
                type="primary" 
                icon={hasCustomForms ? <RightOutlined /> : <CheckCircleOutlined />} 
                onClick={handleNext}
                loading={loading || loadingForms}
              >
                {hasCustomForms ? 'Next' : 'Create Booking'}
              </Button>
            )}
            {currentStep === 2 && hasCustomForms && (
              <Button 
                type="primary" 
                icon={<CheckCircleOutlined />} 
                onClick={handleSubmit}
                loading={loading}
              >
                Create Booking
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modals */}
      <DoubleBookConfirmModal
        visible={doubleBookConfirmVisible}
        onCancel={() => { setDoubleBookConfirmVisible(false); setPendingDoubleBookSlot(null); }}
        onConfirm={handleConfirmDoubleBook}
        pendingSlot={pendingDoubleBookSlot}
      />
      <BlockedDateConfirmModal
        visible={blockedDateConfirmVisible}
        onCancel={() => { setBlockedDateConfirmVisible(false); setPendingBlockedDate(null); }}
        onConfirm={handleConfirmBlockedDate}
        pendingDate={pendingBlockedDate}
      />
    </Modal>
  );
};

export default CreateBookingModal;
